// What the book knows about one position: how often it is reached, what it
// is worth, what to play, and what is played from it — at your level and by
// masters.

import { useEffect, useRef, useState } from "react";
import { errorText, getJson, isSignedOut } from "../app/api.ts";
import { chosenBand, currentBand } from "../app/book.ts";
import { bandLabel, fmtCount, fmtScore, pct } from "../app/format.ts";
import { Loading, Notice } from "../app/ui.tsx";
import type { Score } from "./lines.ts";

interface Stat {
  games: number;
  white: number;
  draws: number;
}

export interface BookMove {
  uci: string;
  san: string;
  name?: string;
  band?: Stat;
  all?: Stat;
  masters?: Stat;
  band_share?: number;
  masters_share?: number;
  white_eval?: Score | null;
}

export interface BookPosition {
  fen: string;
  name?: string;
  in_book: boolean;
  games?: { band?: number; all?: number; masters?: number };
  white_eval?: Score | null;
  depth?: number;
  moves?: BookMove[];
  recommended?: { uci: string; san: string; comment?: string }[];
}

const stat = (s?: Stat) => (s && s.games ? s : null);
const whiteScore = (s?: Stat) => (s && s.games ? (s.white + s.draws / 2) / s.games : null);

export function BookPanel({
  fen,
  version = 0,
  onAdd,
  inLine,
  onData,
}: {
  fen: string;
  /** Changed to read the same position again, after the line changed. */
  version?: number;
  /** A move put into the line straight from the book. */
  onAdd?: (move: BookMove, data: BookPosition) => void;
  /** Whether a move is in the line already: it has nothing to add. */
  inLine?: (uci: string) => boolean;
  onData?: (data: BookPosition, band: string) => void;
}) {
  const [state, setState] = useState<{ data: BookPosition; band: string } | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const seq = useRef(0);
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  useEffect(() => {
    const mine = ++seq.current;
    // The old table stays, dimmed, until the new one arrives. Emptying it
    // shrinks the column, and a page whose height that column set jumps.
    setLoading(true);
    (async () => {
      try {
        const band = await currentBand();
        const q = new URLSearchParams({ fen });
        if (chosenBand()) q.set("band", chosenBand());
        const data = await getJson<BookPosition>("/api/openings/position?" + q);
        if (mine !== seq.current) return;
        setState({ data, band });
        setError("");
        setLoading(false);
        onDataRef.current?.(data, band);
      } catch (err) {
        if (isSignedOut(err) || mine !== seq.current) return;
        setLoading(false);
        setError(errorText(err));
      }
    })();
  }, [fen, version]);

  if (error) {
    return (
      <div className="book-panel">
        <Notice kind="error">{error}</Notice>
      </div>
    );
  }
  if (!state) {
    return (
      <div className="book-panel">
        <Loading text="Reading the book…" />
      </div>
    );
  }
  const { data, band } = state;
  const moves = data.moves || [];
  const shown = showAll ? moves : moves.slice(0, 8);
  const head = (
    <div className="book-head">
      {data.name ? <div className="book-name">{data.name}</div> : null}
      <div className="book-sub">
        {data.in_book ? (
          <>
            <span>
              <b className="num">{fmtCount(data.games?.band)}</b> games at {bandLabel(band) || "your level"}
            </span>
            <span>
              <b className="num">{fmtCount(data.games?.masters)}</b> by masters
            </span>
            {data.white_eval ? (
              <span title={`Stockfish, depth ${data.depth || "?"}. From White's side.`}>
                <b className="num">{fmtScore(data.white_eval)}</b>
              </span>
            ) : null}
          </>
        ) : (
          <span>Not in the book: too few games reach this position.</span>
        )}
      </div>
    </div>
  );
  if (!data.in_book || !moves.length) {
    return <div className={"book-panel" + (loading ? " loading" : "")}>{head}</div>;
  }
  // What to play, above what is played: the move a course built from this
  // position would teach, with the others it could have.
  const mover = String(data.fen).split(/\s+/)[1] === "b" ? "Black" : "White";
  const picks = (data.recommended || []).filter((r) => r.san);
  const pickOf = new Map(picks.map((r, i) => [r.uci, i]));
  return (
    <div className={"book-panel" + (loading ? " loading" : "")}>
      {head}
      {picks.length ? (
        <div className="book-recommended">
          <div className="book-recommended-head">Recommended for {mover}</div>
          {picks.map((r, i) => (
            <div key={r.uci} className={"book-pick" + (i ? " also" : "")}>
              <b className="san">{r.san}</b>
              <span>{r.comment || ""}</span>
            </div>
          ))}
        </div>
      ) : null}
      <table className="book-moves">
        <thead>
          <tr>
            <th>Move</th>
            <th title="Share of games at your level">Your level</th>
            <th title="Share of master games">Masters</th>
            <th title="White's score at your level">White</th>
            <th title="Evaluation after the move, from White's side">Eval</th>
            {onAdd ? <th className="sr-only">Add</th> : null}
          </tr>
        </thead>
        <tbody>
          {shown.map((m) => {
            const score = whiteScore(m.band) ?? whiteScore(m.all);
            return (
              <tr key={m.uci} title={m.name}>
                <td className={"san" + (pickOf.has(m.uci) ? " pick" : "")}>{m.san}</td>
                <td className="num">{stat(m.band) ? pct(m.band_share) : "—"}</td>
                <td className="num">{stat(m.masters) ? pct(m.masters_share) : "—"}</td>
                <td className="num">{score == null ? "—" : pct(score)}</td>
                <td className="num">{m.white_eval ? fmtScore(m.white_eval) : "—"}</td>
                {/* Add a move straight from the book: the reply you want to be
                    ready for, or the move you actually play here. */}
                {onAdd ? (
                  <td className="book-add">
                    {inLine?.(m.uci) ? (
                      <span className="book-in-line" title={`${m.san} is in your line`} aria-label={`${m.san} is in your line`}>
                        ✓
                      </span>
                    ) : (
                      <button type="button" className="more-lines" title={`Add ${m.san} to this line`} onClick={() => onAdd(m, data)}>
                        +
                      </button>
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      {moves.length > 8 ? (
        <button type="button" className="link-btn book-more" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Fewer moves" : `All ${moves.length} moves`}
        </button>
      ) : null}
      {onAdd ? <p className="help book-add-help">＋ puts a move into this line, with the book's answer to it.</p> : null}
    </div>
  );
}
